export interface LaneCount {
  lane: string;
  count: number;
}

export interface ReviewLaneReach {
  reviewLane: string;
  lanes: string[];
}

/** One team-directory profile, as a hash of its address. The documents hold no name or address. */
export interface DiscoverySphereViewer {
  identityHash: string;
  lanes: string[];
  reviewBy: string;
}

export interface DiscoverySphereDecision {
  decisionId: string;
  line: number;
  text: string;
  /** `Proposed`, `Inferred` or `Open`; empty when the provenance cell could not be read. */
  provenance: string;
  provenanceAsWritten: string;
  reviewLaneAsWritten: string;
  lanes: string[];
  unread: string[];
  affects: string;
}

export interface DiscoverySphere {
  id: string;
  repository: string;
  branch: string;
  spherePath: string;
  domain: string;
  title: string;
  lifecycle: string;
  verifiedOn: string;
  readingVersion: string;
  maxAgeDays: number;
  decisionTableRead: boolean;
  decisionsRead: number;
  confirmed: number;
  provenanceNotRead: number;
  waiting: number;
  waitingWithoutLane: number;
  lanes: string[];
  waitingByLane: LaneCount[];
  decisions: DiscoverySphereDecision[];
  directoryLanes: string[];
  reach: ReviewLaneReach[];
  viewers: DiscoverySphereViewer[];
  sourceCommit: string;
  sourceCommitTime: string;
  present: boolean;
}

export interface LaneSummary {
  lane: string;
  spheres: number;
  decisions: number;
}

export interface DiscoverySphereCard {
  sphere: DiscoverySphere;
  /** How many of the sphere's waiting decisions reach the selected lanes. */
  reaching: number;
}

/** The selected lanes and why. No lane selected means all lanes. */
export interface LaneSelection {
  lanes: string[];
  reason: string;
}

type Fields = Record<string, unknown>;

const isRecord = (value: unknown): value is Fields =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const record = (value: unknown): Fields => (isRecord(value) ? value : {});
const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const flag = (value: unknown): boolean => value === true;
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const texts = (value: unknown): string[] =>
  list(value).filter((item): item is string => typeof item === 'string');

function count(value: unknown): number {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 0;
}

function laneCounts(value: unknown): LaneCount[] {
  return list(value)
    .filter(isRecord)
    .map((item) => ({ lane: text(item['lane']), count: count(item['count']) }))
    .filter((item) => item.lane !== '');
}

/** Maps one sphere document. A partial document never throws: a missing field gets its empty value. */
export function discoverySphereFrom(id: string, data: unknown): DiscoverySphere {
  const fields = record(data);
  return {
    id,
    repository: text(fields['repository']),
    branch: text(fields['branch']),
    spherePath: text(fields['sphere_path']),
    domain: text(fields['domain']),
    title: text(fields['title']),
    lifecycle: text(fields['lifecycle']),
    verifiedOn: text(fields['verified_on']),
    readingVersion: text(fields['reading_version']),
    maxAgeDays: count(fields['max_age_days']),
    decisionTableRead: flag(fields['decision_table_read']),
    decisionsRead: count(fields['decisions_read']),
    confirmed: count(fields['confirmed']),
    provenanceNotRead: count(fields['provenance_not_read']),
    waiting: count(fields['waiting']),
    waitingWithoutLane: count(fields['waiting_without_lane']),
    lanes: texts(fields['lanes']),
    waitingByLane: laneCounts(fields['waiting_by_lane']),
    decisions: list(fields['decisions'])
      .filter(isRecord)
      .map((item) => ({
        decisionId: text(item['decision_id']),
        line: count(item['line']),
        text: text(item['text']),
        provenance: text(item['provenance']),
        provenanceAsWritten: text(item['provenance_as_written']),
        reviewLaneAsWritten: text(item['review_lane_as_written']),
        lanes: texts(item['lanes']),
        unread: texts(item['unread']),
        affects: text(item['affects']),
      })),
    directoryLanes: texts(fields['directory_lanes']),
    reach: list(fields['reach'])
      .filter(isRecord)
      .map((item) => ({ reviewLane: text(item['review_lane']), lanes: texts(item['lanes']) })),
    viewers: list(fields['viewers'])
      .filter(isRecord)
      .map((item) => ({
        identityHash: text(item['identity_hash']),
        lanes: texts(item['lanes']),
        reviewBy: text(item['review_by']),
      })),
    sourceCommit: text(fields['source_commit']),
    sourceCommitTime: text(fields['source_commit_time']),
    present: flag(fields['present']),
  };
}

/**
 * The present sphere record read from the newest commit, or null when no present sphere has a
 * commit yet. Cross-sphere directory metadata (`directoryLanes`, `viewers`, `maxAgeDays`,
 * `sourceCommit`, `sourceCommitTime`) comes from this record.
 */
export function latestSphere(spheres: readonly DiscoverySphere[]): DiscoverySphere | null {
  let latest: DiscoverySphere | null = null;
  for (const sphere of spheres) {
    if (!sphere.present || !sphere.sourceCommit) continue;
    if (!latest || sphere.sourceCommitTime > latest.sourceCommitTime) {
      latest = sphere;
    }
  }
  return latest;
}

const DAY_MS = 86_400_000;

/** Today's date in UTC as `YYYY-MM-DD`, the form the documents use. */
export function utcDate(today: Date): string {
  return today.toISOString().slice(0, 10);
}

/** Midnight UTC of a `YYYY-MM-DD` date, or null when the text is not such a date. */
function utcDay(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const time = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(time) || utcDate(new Date(time)) !== value ? null : time;
}

/**
 * Whole days from `verifiedOn` to today, both as UTC dates. Null when there is no date. The
 * documents store no age, so that a day passing changes none of them.
 */
export function ageInDays(verifiedOn: string, today: Date): number | null {
  const verified = utcDay(verifiedOn);
  if (verified === null) return null;
  const now = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  // A date written east of UTC can be one day ahead of today in UTC; that is not a negative age.
  return Math.max(0, Math.round((now - verified) / DAY_MS));
}

/** The commit a sphere record was read from, on GitHub. Empty when the document names none. */
export function commitUrl(sphere: Pick<DiscoverySphere, 'repository' | 'sourceCommit'>): string {
  return sphere.repository && sphere.sourceCommit
    ? `https://github.com/${sphere.repository}/commit/${sphere.sourceCommit}`
    : '';
}

/**
 * The sphere's pitch at the commit its record was read from, and with a line the row that states
 * a decision. Line numbers belong to that commit.
 */
export function pitchUrl(
  sphere: Pick<DiscoverySphere, 'repository' | 'sourceCommit' | 'spherePath'>,
  line = 0,
): string {
  if (!sphere.repository || !sphere.sourceCommit || !sphere.spherePath) return '';
  const url = `https://github.com/${sphere.repository}/blob/${sphere.sourceCommit}/${sphere.spherePath}/pitch.md`;
  return line > 0 ? `${url}#L${line}` : url;
}

/**
 * Router link to the Problems page of a sphere. The key is the one decision cards created from a
 * decision register carry (`problemKeyOf`), encoded as the Problems list encodes it.
 */
export function problemsLink(sphere: Pick<DiscoverySphere, 'repository' | 'spherePath'>): string[] {
  return ['/problems', encodeURIComponent(`${sphere.repository}|${sphere.spherePath}`)];
}

/**
 * The hash a sphere's `viewers` carry: SHA-256, in lowercase hexadecimal, of the address after
 * trimming and lowercasing it. Empty when there is no address.
 */
export async function identityHash(email: string | null | undefined): Promise<string> {
  const address = (email ?? '').trim().toLowerCase();
  if (!address) return '';
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(address));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Which lanes are selected: the viewer's last explicit choice, else the viewer's own unexpired
 * team-directory lanes, else all lanes. `choice` is null when the viewer never chose; an empty
 * choice is the explicit choice of all lanes. Lanes the directory no longer names are ignored.
 */
export function selectLanes(
  sphere: Pick<DiscoverySphere, 'directoryLanes' | 'viewers'>,
  choice: readonly string[] | null,
  viewerHash: string,
  today: string,
): LaneSelection {
  const known = (lanes: readonly string[]) =>
    sphere.directoryLanes.filter((lane) => lanes.includes(lane));
  if (choice) {
    const chosen = known(choice);
    if (chosen.length || !choice.length) return { lanes: chosen, reason: 'your last choice' };
  }
  const viewer = viewerHash
    ? sphere.viewers.find((item) => item.identityHash === viewerHash)
    : undefined;
  const own = viewer ? known(viewer.lanes) : [];
  if (!viewer || !own.length)
    return { lanes: [], reason: 'all lanes: your address matches no team-directory profile' };
  if (utcDay(viewer.reviewBy) === null)
    return { lanes: [], reason: 'all lanes: your team-directory profile has no review date' };
  if (viewer.reviewBy < today)
    return {
      lanes: [],
      reason: `all lanes: your team-directory profile was due for review on ${viewer.reviewBy}`,
    };
  return { lanes: own, reason: 'your lanes in the team directory' };
}

/** How many of a sphere's waiting decisions reach the selected lanes. No lane means all lanes. */
export function reachingCount(sphere: DiscoverySphere, lanes: readonly string[]): number {
  if (!lanes.length) return sphere.waiting;
  return sphere.decisions.filter(
    (decision) => decision.provenance !== '' && decision.lanes.some((lane) => lanes.includes(lane)),
  ).length;
}

/** By the name shown, which is the id of a record that carries no title. */
export function byTitle(left: DiscoverySphere, right: DiscoverySphere): number {
  return sphereName(left).localeCompare(sphereName(right)) || left.id.localeCompare(right.id);
}

/**
 * One card for each sphere with a waiting decision on the selected lanes, oldest evidence first.
 * A sphere without a verification date comes last, as does one whose date is not a date.
 */
export function cardsFor(
  spheres: readonly DiscoverySphere[],
  lanes: readonly string[],
): DiscoverySphereCard[] {
  return spheres
    .map((sphere) => ({ sphere, reaching: reachingCount(sphere, lanes) }))
    .filter((card) => card.reaching > 0)
    .sort((left, right) => {
      const a = utcDay(left.sphere.verifiedOn) ?? Infinity;
      const b = utcDay(right.sphere.verifiedOn) ?? Infinity;
      return a !== b ? a - b : byTitle(left.sphere, right.sphere);
    });
}

/** `1 decision`, `2 decisions`. */
export function counted(value: number, noun: string): string {
  return `${value} ${noun}${value === 1 ? '' : 's'}`;
}

/** The title, or the id of a record that carries none. */
export function sphereName(sphere: Pick<DiscoverySphere, 'id' | 'title'>): string {
  return sphere.title || sphere.id;
}

/** `platform · discovery`. */
export function sphereKind(sphere: Pick<DiscoverySphere, 'domain' | 'lifecycle'>): string {
  return [sphere.domain, sphere.lifecycle].filter(Boolean).join(' · ');
}
